import SEO from "../components/SEO";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { formatNZDate } from "../utils/timezone";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { 
  ArrowLeft, Building2, Mail, Phone, MapPin, Star, 
  Pencil, MessageSquare, DollarSign, User, Globe, Save, X,
  Sun, Moon, PauseCircle, PlayCircle, AlertTriangle
} from "lucide-react";
import { useAuth, useTheme, API } from "../App";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/qlurr1kb_9985D3C3-CBD5-4831-BB38-56FCD4CA4133.PNG";
const LIGHT_LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/ym9v5wop_7B92B142-D2AF-47C9-93FB-F3C3C1AB10EF.PNG";

export default function ShopAccount() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [shopProfile, setShopProfile] = useState(null);
  const [shopReviews, setShopReviews] = useState({ reviews: [], stats: {} });
  const [stats, setStats] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    shop_name: "",
    contact_name: "",
    phone: "",
    service_radius_km: 50,
    google_maps_url: "",
    address: {
      full_address: "",
      street_number: "",
      street_name: "",
      suburb: "",
      city: "",
      postcode: ""
    }
  });
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState("");
  const [submittingPause, setSubmittingPause] = useState(false);
  const [resuming, setResuming] = useState(false);

  // Check authentication
  useEffect(() => {
    if (!token) {
      navigate("/shop/auth");
    }
  }, [token, navigate]);

  // Apply theme

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, reviewsRes, statsRes] = await Promise.all([
        axios.get(`${API}/shop/profile`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/shop/reviews`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/shop/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (profileRes.data) {
        setShopProfile(profileRes.data);
        setProfileForm({
          shop_name: profileRes.data.shop_name || "",
          contact_name: profileRes.data.contact_name || "",
          phone: profileRes.data.phone || "",
          service_radius_km: profileRes.data.service_radius_km || 50,
          google_maps_url: profileRes.data.google_maps_url || "",
          address: profileRes.data.address || {
            full_address: "",
            street_number: "",
            street_name: "",
            suburb: "",
            city: "",
            postcode: ""
          }
        });
      }
      if (reviewsRes.data) {
        setShopReviews(reviewsRes.data);
      }
      if (statsRes.data) {
        setStats(statsRes.data);
      }
    } catch (error) {
      toast.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, fetchData]);

  const handleUpdateProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await axios.put(
        `${API}/shop/profile`,
        profileForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShopProfile(response.data.shop);
      setEditingProfile(false);
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePauseAccount = async () => {
    if (!pauseReason || pauseReason.trim().length < 10) {
      toast.error("Please provide a reason (minimum 10 characters)");
      return;
    }
    setSubmittingPause(true);
    try {
      await axios.post(
        `${API}/shop/request-pause`,
        { reason: pauseReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Your account has been paused. You will not receive new quote requests.");
      setPauseDialogOpen(false);
      setPauseReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to pause account");
    } finally {
      setSubmittingPause(false);
    }
  };

  const handleResumeAccount = async () => {
    setResuming(true);
    try {
      await axios.post(
        `${API}/shop/request-resume`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Your account has been resumed! You will now receive new quote requests.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resume account");
    } finally {
      setResuming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO page="shopAccount" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/shop/dashboard")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <img
              src={theme === "dark" ? LOGO_URL : LIGHT_LOGO_URL}
              alt="TyreMatch"
              className="h-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-full">
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Account Settings</h1>
          <p className="text-muted-foreground">Manage your shop profile and view reviews</p>
        </div>

        <div className="space-y-6">
          {/* Shop Profile Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Shop Profile
                </CardTitle>
                {!editingProfile ? (
                  <Button variant="outline" size="sm" onClick={() => setEditingProfile(true)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit Profile
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleUpdateProfile} disabled={savingProfile}>
                      <Save className="h-4 w-4 mr-2" />
                      {savingProfile ? "Saving..." : "Save"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => {
                      setEditingProfile(false);
                      setProfileForm({
                        shop_name: shopProfile?.shop_name || "",
                        contact_name: shopProfile?.contact_name || "",
                        phone: shopProfile?.phone || "",
                        service_radius_km: shopProfile?.service_radius_km || 50,
                        google_maps_url: shopProfile?.google_maps_url || "",
                        address: shopProfile?.address || {}
                      });
                    }}>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingProfile ? (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="shop_name">Shop Name *</Label>
                      <Input
                        id="shop_name"
                        value={profileForm.shop_name}
                        onChange={(e) => setProfileForm({ ...profileForm, shop_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_name">Contact Name</Label>
                      <Input
                        id="contact_name"
                        placeholder="Full name of primary contact"
                        value={profileForm.contact_name}
                        onChange={(e) => setProfileForm({ ...profileForm, contact_name: e.target.value })}
                        data-testid="shop-contact-name-input"
                      />
                      {!profileForm.contact_name && (
                        <p className="text-xs text-amber-600 mt-1">Please add a contact name for your shop</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="service_radius">Service Radius (km)</Label>
                      <Input
                        id="service_radius"
                        type="number"
                        value={profileForm.service_radius_km}
                        onChange={(e) => setProfileForm({ ...profileForm, service_radius_km: parseInt(e.target.value) || 50 })}
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label>Shop Address *</Label>
                      <AddressAutocomplete
                        value={profileForm.address}
                        onChange={(address) => setProfileForm({ ...profileForm, address })}
                        placeholder="Start typing your shop address..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="google_maps_url">Google Maps Link</Label>
                      <Input
                        id="google_maps_url"
                        value={profileForm.google_maps_url}
                        onChange={(e) => setProfileForm({ ...profileForm, google_maps_url: e.target.value })}
                        placeholder="https://maps.google.com/..."
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Paste your Google Maps business link so customers can find you easily
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">Shop Name</p>
                        <p className="font-medium truncate">{shopProfile?.shop_name || "N/A"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="font-medium truncate">{shopProfile?.email || "N/A"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <p className="font-medium">{shopProfile?.phone || "N/A"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">Service Radius</p>
                        <p className="font-medium">{shopProfile?.service_radius_km || 50} km</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                      <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">Address</p>
                        <p className="font-medium">{shopProfile?.address?.full_address || "N/A"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">Google Maps</p>
                        {shopProfile?.google_maps_url ? (
                          <a 
                            href={shopProfile.google_maps_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-medium text-primary hover:underline truncate block"
                          >
                            View on Maps →
                          </a>
                        ) : (
                          <p className="font-medium text-muted-foreground">Not set</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">Member Since</p>
                        <p className="font-medium">
                          {shopProfile?.join_date ? formatNZDate(shopProfile.join_date) : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground mb-1">Status</p>
                        <Badge variant={shopProfile?.approval_status === "approved" ? "default" : "secondary"}>
                          {shopProfile?.approval_status ? shopProfile.approval_status.charAt(0).toUpperCase() + shopProfile.approval_status.slice(1) : "Pending"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Account Status & Pause/Resume Card */}
          <Card className={shopProfile?.approval_status === "suspended" && shopProfile?.pause_requested ? "border-amber-500/50" : ""}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {shopProfile?.pause_requested ? <PauseCircle className="h-5 w-5 text-amber-500" /> : <PlayCircle className="h-5 w-5 text-green-500" />}
                Account Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              {shopProfile?.approval_status === "suspended" && shopProfile?.pause_requested ? (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-amber-600 dark:text-amber-400">Account Paused</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Your account is currently paused. You will not receive new quote requests or email notifications while paused.
                      </p>
                      {shopProfile?.pause_reason && (
                        <p className="text-sm text-muted-foreground mt-2">
                          <strong>Reason:</strong> {shopProfile.pause_reason}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button onClick={handleResumeAccount} disabled={resuming} className="w-full sm:w-auto" data-testid="resume-account-btn">
                    <PlayCircle className="h-4 w-4 mr-2" />
                    {resuming ? "Resuming..." : "Resume Account"}
                  </Button>
                </div>
              ) : shopProfile?.approval_status === "suspended" ? (
                <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-red-600 dark:text-red-400">Account Suspended by Admin</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Your account has been suspended by an administrator. Please contact support for assistance.
                    </p>
                  </div>
                </div>
              ) : shopProfile?.approval_status === "approved" ? (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <PlayCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-green-600 dark:text-green-400">Account Active</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Your account is active and receiving new quote requests.
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => setPauseDialogOpen(true)} className="w-full sm:w-auto" data-testid="pause-account-btn">
                    <PauseCircle className="h-4 w-4 mr-2" />
                    Pause Account
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Pausing your account will stop all new quote requests and email notifications. You can resume anytime.
                  </p>
                </div>
              ) : (
                <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-yellow-600 dark:text-yellow-400">Pending Approval</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Your shop registration is pending approval. You will be notified once approved.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stats Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Performance & Commission
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Commission Rate</p>
                  <p className="text-2xl font-bold text-primary">{shopProfile?.commission_rate || 0}%</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Total Revenue</p>
                  <p className="text-2xl font-bold">${stats?.total_revenue?.toFixed(0) || "0"}</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Quotes Sent</p>
                  <p className="text-2xl font-bold">{stats?.total_quotes || 0}</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Orders Won</p>
                  <p className="text-2xl font-bold">{stats?.total_orders || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Reviews Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5" />
                Customer Reviews
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Rating Summary */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-6 p-4 bg-muted/50 rounded-lg">
                <div className="text-center">
                  <p className="text-5xl font-bold text-primary">
                    {shopReviews.stats?.avg_rating?.toFixed(1) || "0.0"}
                  </p>
                  <div className="flex items-center justify-center gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-5 w-5 ${
                          star <= (shopReviews.stats?.avg_rating || 0)
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-300"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {shopReviews.stats?.total_reviews || 0} reviews
                  </p>
                </div>
                <div className="flex-1 space-y-2 w-full sm:w-auto">
                  {[5, 4, 3, 2, 1].map((rating) => (
                    <div key={rating} className="flex items-center gap-2">
                      <span className="text-sm w-3">{rating}</span>
                      <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-yellow-400 h-2 rounded-full transition-all"
                          style={{
                            width: `${
                              shopReviews.stats?.total_reviews > 0
                                ? (shopReviews.stats?.rating_distribution?.[rating] || 0) / shopReviews.stats.total_reviews * 100
                                : 0
                            }%`
                          }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground w-8 text-right">
                        {shopReviews.stats?.rating_distribution?.[rating] || 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reviews List */}
              <div className="space-y-4">
                {shopReviews.reviews?.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">No reviews yet</p>
                    <p className="text-sm">Complete orders to receive customer reviews</p>
                  </div>
                ) : (
                  shopReviews.reviews?.map((review, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`h-5 w-5 ${
                                star <= review.rating
                                  ? "fill-yellow-400 text-yellow-400"
                                  : "text-gray-300"
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {review.created_at ? formatNZDate(review.created_at) : ""}
                        </span>
                      </div>
                      {review.comments ? (
                        <p className="text-foreground">{review.comments}</p>
                      ) : (
                        <p className="text-muted-foreground italic">No comment provided</p>
                      )}
                      {review.order_amount && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Order value: ${review.order_amount.toFixed(2)}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Pause Account Dialog */}
      <Dialog open={pauseDialogOpen} onOpenChange={setPauseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pause Your Account</DialogTitle>
            <DialogDescription>
              While paused, you will not receive new quote requests or email notifications. You can resume your account at any time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="pause-reason">Reason for pausing (required)</Label>
              <Textarea
                id="pause-reason"
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
                placeholder="e.g. Going on holiday, temporarily closing for renovations..."
                className="mt-2"
                data-testid="pause-reason-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Minimum 10 characters</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPauseDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePauseAccount}
              disabled={submittingPause || pauseReason.trim().length < 10}
              variant="destructive"
              data-testid="confirm-pause-btn"
            >
              {submittingPause ? "Pausing..." : "Pause Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
